/**
 * Skeleton and bone manipulation utilities for procedural animation
 */
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { Bone } from "@babylonjs/core/Bones/bone";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Scene } from "@babylonjs/core/scene";

/**
 * Information about a bone in the skeleton
 */
export interface BoneInfo {
    name: string;
    index: number;
    parentName: string | null;
    parentIndex: number;
    childCount: number;
    localPosition: Vector3;
    localRotation: Quaternion;
    worldPosition: Vector3;
}

/**
 * Skeleton analysis result
 */
export interface SkeletonAnalysis {
    boneCount: number;
    rootBones: string[];
    bones: BoneInfo[];
    hierarchy: Map<string, string[]>; // parent -> children
    commonBones: {
        head?: string;
        spine?: string;
        hips?: string;
        leftHand?: string;
        rightHand?: string;
        leftFoot?: string;
        rightFoot?: string;
    };
}

/**
 * Utilities for working with skeletons and bones
 */
export class SkeletonUtils {
    
    /**
     * Analyze a skeleton and return detailed information
     */
    public static analyzeSkeleton(skeleton: Skeleton): SkeletonAnalysis {
        const bones = skeleton.bones;
        const boneInfos: BoneInfo[] = [];
        const hierarchy = new Map<string, string[]>();
        const rootBones: string[] = [];

        // Build bone info and hierarchy
        bones.forEach((bone, index) => {
            const parent = bone.getParent();
            const parentName = parent?.name || null;
            const parentIndex = parent ? bones.indexOf(parent) : -1;

            boneInfos.push({
                name: bone.name,
                index,
                parentName,
                parentIndex,
                childCount: bone.children.length,
                localPosition: bone.getPosition(0),  // Local space
                localRotation: bone.getRotationQuaternion(0),
                worldPosition: bone.getAbsolutePosition(),
            });

            // Track root bones
            if (!parent) {
                rootBones.push(bone.name);
            }

            // Build hierarchy map
            if (parentName) {
                if (!hierarchy.has(parentName)) {
                    hierarchy.set(parentName, []);
                }
                hierarchy.get(parentName)!.push(bone.name);
            }
        });

        // Try to identify common bones by name patterns
        const commonBones = this._identifyCommonBones(bones);

        return {
            boneCount: bones.length,
            rootBones,
            bones: boneInfos,
            hierarchy,
            commonBones,
        };
    }

    /**
     * Identify common bones by name patterns (head, spine, hands, feet, etc.)
     */
    private static _identifyCommonBones(bones: Bone[]): SkeletonAnalysis["commonBones"] {
        const result: SkeletonAnalysis["commonBones"] = {};
        const boneNames = bones.map(b => b.name.toLowerCase());

        // Common patterns for bone names
        const patterns = {
            head: ["head", "skull", "cranium"],
            spine: ["spine", "torso", "chest", "back"],
            hips: ["hip", "pelvis", "root"],
            leftHand: ["hand.l", "hand_l", "lefthand", "l_hand", "l.hand"],
            rightHand: ["hand.r", "hand_r", "righthand", "r_hand", "r.hand"],
            leftFoot: ["foot.l", "foot_l", "leftfoot", "l_foot", "l.foot"],
            rightFoot: ["foot.r", "foot_r", "rightfoot", "r_foot", "r.foot"],
        };

        for (const [key, searchPatterns] of Object.entries(patterns)) {
            for (const pattern of searchPatterns) {
                const matchIndex = boneNames.findIndex(name => name.includes(pattern));
                if (matchIndex !== -1) {
                    (result as any)[key] = bones[matchIndex].name;
                    break;
                }
            }
        }

        return result;
    }

    /**
     * Get a bone by name (case-insensitive partial match)
     */
    public static getBone(skeleton: Skeleton, name: string): Bone | null {
        const lowerName = name.toLowerCase();
        return skeleton.bones.find(b => 
            b.name.toLowerCase().includes(lowerName)
        ) || null;
    }

    /**
     * Rotate a bone to look at a target position
     * @param bone The bone to rotate
     * @param targetPosition World position to look at
     * @param mesh The mesh the skeleton is attached to
     * @param _yawAxis Yaw rotation axis (reserved for future use)
     * @param _pitchAxis Pitch rotation axis (reserved for future use)
     * @param maxAngle Maximum rotation angle in radians
     */
    public static lookAt(
        bone: Bone,
        targetPosition: Vector3,
        mesh: TransformNode,
        _yawAxis: Vector3 = Vector3.Up(),
        _pitchAxis: Vector3 = Vector3.Right(),
        maxAngle?: number
    ): void {
        const bonePos = bone.getAbsolutePosition(mesh);
        const direction = targetPosition.subtract(bonePos).normalize();
        
        // Calculate rotation to look at target
        let targetRotation = Quaternion.FromLookDirectionLH(direction, Vector3.Up());
        
        if (maxAngle !== undefined) {
            // Limit rotation angle
            const currentRotation = bone.getRotationQuaternion(0, mesh);
            targetRotation = Quaternion.Slerp(
                currentRotation,
                targetRotation,
                Math.min(1, maxAngle / Math.PI)
            );
        }
        
        bone.setRotationQuaternion(targetRotation, 0, mesh);
    }

    /**
     * Set bone rotation in local space
     */
    public static setLocalRotation(
        bone: Bone,
        rotation: Quaternion,
        mesh?: TransformNode
    ): void {
        bone.setRotationQuaternion(rotation, 0, mesh);
    }

    /**
     * Rotate bone by euler angles (in radians)
     */
    public static rotateByEuler(
        bone: Bone,
        x: number,
        y: number,
        z: number,
        space: "local" | "world" = "local",
        mesh?: TransformNode
    ): void {
        const rotationDelta = Quaternion.FromEulerAngles(x, y, z);
        const currentRotation = bone.getRotationQuaternion(0, mesh);
        
        if (space === "local") {
            bone.setRotationQuaternion(currentRotation.multiply(rotationDelta), 0, mesh);
        } else {
            bone.setRotationQuaternion(rotationDelta.multiply(currentRotation), 0, mesh);
        }
    }

    /**
     * Blend bone rotation toward a target rotation
     */
    public static blendRotation(
        bone: Bone,
        targetRotation: Quaternion,
        blendFactor: number,
        mesh?: TransformNode
    ): void {
        const currentRotation = bone.getRotationQuaternion(0, mesh);
        const blendedRotation = Quaternion.Slerp(
            currentRotation,
            targetRotation,
            Math.max(0, Math.min(1, blendFactor))
        );
        bone.setRotationQuaternion(blendedRotation, 0, mesh);
    }

    /**
     * Copy pose from one skeleton to another (useful for retargeting)
     */
    public static copyPose(
        sourceSkeleton: Skeleton,
        targetSkeleton: Skeleton,
        boneMapping?: Map<string, string>
    ): void {
        const sourceBonesMap = new Map(sourceSkeleton.bones.map(b => [b.name, b]));
        
        for (const targetBone of targetSkeleton.bones) {
            let sourceBoneName = targetBone.name;
            
            // Use mapping if provided
            if (boneMapping?.has(targetBone.name)) {
                sourceBoneName = boneMapping.get(targetBone.name)!;
            }
            
            const sourceBone = sourceBonesMap.get(sourceBoneName);
            if (sourceBone) {
                targetBone.setRotationQuaternion(sourceBone.getRotationQuaternion(0), 0);
            }
        }
    }

    /**
     * Create a bone debug visualization
     * @param skeleton The skeleton to visualize
     * @param scene The Babylon scene
     * @param _size Size of debug markers (reserved for future visual markers)
     */
    public static createDebugVisualization(
        skeleton: Skeleton,
        scene: Scene,
        _size: number = 0.1
    ): TransformNode {
        // Enable skeleton bone visualization
        skeleton.returnToRest();
        
        const debugRoot = new TransformNode("skeletonDebug", scene);
        
        // Use Babylon's built-in skeleton viewer if needed
        // For now, just log the skeleton structure
        console.log("Skeleton structure:");
        const analysis = this.analyzeSkeleton(skeleton);
        analysis.bones.forEach(bone => {
            const indent = "  ".repeat(bone.parentIndex === -1 ? 0 : 1);
            console.log(`${indent}${bone.name} (children: ${bone.childCount})`);
        });
        
        return debugRoot;
    }

    /**
     * Print skeleton hierarchy to console
     */
    public static printHierarchy(skeleton: Skeleton): void {
        const analysis = this.analyzeSkeleton(skeleton);
        
        console.log("=== Skeleton Hierarchy ===");
        console.log(`Total bones: ${analysis.boneCount}`);
        console.log(`Root bones: ${analysis.rootBones.join(", ")}`);
        console.log("");
        
        // Print tree structure
        const printBone = (boneName: string, indent: number = 0) => {
            const prefix = "  ".repeat(indent) + (indent > 0 ? "├─ " : "");
            console.log(`${prefix}${boneName}`);
            
            const children = analysis.hierarchy.get(boneName) || [];
            children.forEach(child => printBone(child, indent + 1));
        };
        
        analysis.rootBones.forEach(root => printBone(root));
        
        console.log("");
        console.log("Common bones detected:", analysis.commonBones);
    }
}
